import { useCallback } from "react";

import {
  isMockTemplatePackAllowedForPlan,
  mockTemplatePacks,
} from "@/app/library/data_sources/mock";
import { PlanDefinitionsStore } from "@/app/plans/data_sources/store";
import type { APIPlanDefinitionResponseType } from "@/core/data_sources/models/APIPlanDefinition";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import {
  AdminMiniList,
  AdminMiniRow,
  AdminPanel,
  PageHeader,
} from "@/core/ui/molecules";
import { DialogTemplate } from "@/core/ui/templates";
import { useStore } from "@/core/utils/hooks";

import { plans, type Plan } from "../../utils";
import { PlanEditorDialog } from "./components/PlanEditorDialog";

export function PlansPage() {
  const { actions: appActions } = useStore(AppStore.instance);
  const {
    actions: planActions,
    state,
    hooks,
  } = useStore(PlanDefinitionsStore.instance);
  hooks.usePlanDefinitionsStoreService({
    page: 1,
    page_size: 100,
  });
  const closeDialog = useCallback(() => {
    appActions.dialog("close");
  }, [appActions]);
  const openPlanEditor = useCallback(
    (entry: PlanRowEntry) => {
      planActions.setPlan(entry.apiPlan ?? null);
      appActions.dialog(
        "open",
        <DialogTemplate className="max-w-none">
          <PlanEditorDialog
            apiPlan={entry.apiPlan}
            onClose={closeDialog}
            plan={entry.plan}
          />
        </DialogTemplate>,
      );
    },
    [appActions, closeDialog, planActions],
  );
  const planRows: PlanRowEntry[] = state?.list.length
    ? state.list.map((plan) => {
        const data = plan.getData();
        return { apiPlan: data, plan: toPlanRow(data) };
      })
    : plans.map((plan) => ({ plan }));

  return (
    <>
      <PageHeader
        actions={<Button>Reset filters</Button>}
        breadcrumb={["Super Admin", "Plans"]}
        subtitle="Plan Engine controls modules, flags, limits and which Template Packs are available."
        title="Plans"
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {planRows.map((entry) => (
          <PlanAvailabilityCard
            key={entry.plan.id}
            onOpen={() => openPlanEditor(entry)}
            plan={entry.plan}
          />
        ))}
      </div>
    </>
  );
}

type PlanRowEntry = {
  readonly apiPlan?: APIPlanDefinitionResponseType;
  readonly plan: Plan;
};

function PlanAvailabilityCard({
  onOpen,
  plan,
}: {
  readonly onOpen: () => void;
  readonly plan: Plan;
}) {
  const allowedPacks = mockTemplatePacks.filter((pack) =>
    isMockTemplatePackAllowedForPlan(pack, plan.name),
  ).length;

  return (
    <AdminPanel className="p-[18px]">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="font-extrabold text-ink">{plan.name}</div>
          <div className="text-xs text-ink-muted">
            v{plan.activeVersion} · {plan.tenantsCount} tenants
          </div>
        </div>
        <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-extrabold text-emerald-700">
          Published
        </span>
      </div>
      <div className="mb-4 text-[22px] font-extrabold text-ink">
        {plan.priceLabel === "Contact" ? "Custom" : plan.priceLabel}
      </div>
      <AdminMiniList>
        <AdminMiniRow>
          <strong>Allowed packs</strong>
          <span>{allowedPacks}</span>
        </AdminMiniRow>
        <AdminMiniRow>
          <strong>Users</strong>
          <span>{getPlanLimit(plan.name, "users")}</span>
        </AdminMiniRow>
        <AdminMiniRow>
          <strong>Workspaces</strong>
          <span>{getPlanLimit(plan.name, "workspaces")}</span>
        </AdminMiniRow>
      </AdminMiniList>
      <Button className="mt-4" onClick={onOpen} variant="primary">
        Open Plan Editor
      </Button>
    </AdminPanel>
  );
}

function getPlanLimit(plan: Plan["name"], key: "users" | "workspaces") {
  const limits = {
    Enterprise: { users: "Unlimited", workspaces: "Unlimited" },
    Go: { users: 5, workspaces: 1 },
    Growth: { users: 25, workspaces: 3 },
    Scale: { users: 100, workspaces: 10 },
  } as const;

  return limits[plan][key];
}

function toPlanRow(plan: APIPlanDefinitionResponseType): Plan {
  const normalizedName = normalizePlanName(plan.name);
  return {
    activeVersion: plan.current_version ?? 0,
    commercial: plan.description ?? "",
    description: plan.description ?? "",
    id: (
      plan.plan_code ?? String(plan.id ?? normalizedName)
    ).toLowerCase() as Plan["id"],
    name: normalizedName,
    price: "Contact",
    priceLabel: "Contact",
    publishedAt:
      plan.modified?.split("T")[0] ?? plan.created?.split("T")[0] ?? "-",
    publishedBy: "-",
    status: "active",
    stripeProductId: "-",
    tenantsCount: 0,
  };
}

function normalizePlanName(name?: string): Plan["name"] {
  const normalized = name?.toLowerCase();
  if (normalized === "go") {
    return "Go";
  }
  if (normalized === "growth") {
    return "Growth";
  }
  if (normalized === "scale") {
    return "Scale";
  }
  return "Enterprise";
}
